import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import { Loader2 } from 'lucide-react';

// Pages
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import HomePage from './pages/HomePage';
import CalendarPage from './pages/CalendarPage';
import BookingDetailPage from './pages/BookingDetailPage';
import PrepPage from './pages/PrepPage';
import ShortLinkRedirect from './pages/ShortLinkRedirect';
import SurveyPage from './pages/SurveyPage';
import InvitePage from './pages/InvitePage';
import AdminLayout, { AdminDashboard } from './pages/AdminLayout';
import AdminUsersPage from './pages/AdminUsersPage';
import AdminUserDetailPage from './pages/AdminUserDetailPage';
import AdminHubhusPage from './pages/AdminHubhusPage';
import AdminLogsPage from './pages/AdminLogsPage';
import AdminTestPage from './pages/AdminTestPage';
import AdminSurveyPage from './pages/AdminSurveyPage';
import AdminSurveyLogPage from './pages/AdminSurveyLogPage';
import AdminEmailTemplatePage from './pages/AdminEmailTemplatePage';
import TacticsPage from './pages/admin/TacticsPage';
import DebugPage from './pages/admin/DebugPage';
import ProductCategoriesPage from './pages/admin/ProductCategoriesPage';
import ProductKnowledgeAdminPage from './pages/admin/ProductKnowledgeAdminPage';
import ExperimentDashboardPage from './pages/admin/ExperimentDashboardPage';
import EmailFlowPage from './pages/admin/EmailFlowPage';
import SurveyDuplicatesPage from './pages/admin/SurveyDuplicatesPage';
import SatisfactionDashboard from './pages/admin/SatisfactionDashboard';
import ImageUploadPage from './pages/ImageUploadPage';
import ProductKnowledgePage from './pages/ProductKnowledgePage';
import ImageGalleryPage from './pages/ImageGalleryPage';
import FeedbackPage from './pages/FeedbackPage';
// PWA install prompt disabled - must not be shown on customer-facing pages

// Protected Route wrapper
const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { user, loading, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (requireAdmin && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Public Route wrapper (redirect if already logged in)
const PublicRoute = ({ children }) => {
  const { user, loading, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (user) {
    // Redirect admin to admin dashboard, others to calendar
    return <Navigate to={isAdmin ? "/admin" : "/"} replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        } 
      />
      <Route 
        path="/forgot-password" 
        element={
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        } 
      />
      <Route path="/survey/:token" element={<SurveyPage />} />
      <Route path="/invite/:token" element={<InvitePage />} />
      <Route path="/prep/:appointmentId" element={<PrepPage />} />
      <Route path="/p/:code" element={<ShortLinkRedirect />} />
      <Route path="/feedback/:token" element={<FeedbackPage />} />

      {/* Protected Routes - Consultant/Installer */}
      <Route 
        path="/" 
        element={
          <ProtectedRoute>
            <HomePage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/calendar" 
        element={
          <ProtectedRoute>
            <CalendarPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/booking/:hubhusBookingId" 
        element={
          <ProtectedRoute>
            <BookingDetailPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/upload" 
        element={
          <ProtectedRoute>
            <ImageUploadPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/produktviden" 
        element={
          <ProtectedRoute>
            <ProductKnowledgePage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/billeder" 
        element={
          <ProtectedRoute>
            <ImageGalleryPage />
          </ProtectedRoute>
        } 
      />

      {/* Protected Routes - Admin */}
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute requireAdmin>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<AdminDashboard />} />
        <Route path="test" element={<AdminTestPage />} />
        <Route path="users" element={<AdminUsersPage />} />
        <Route path="users/:userId" element={<AdminUserDetailPage />} />
        <Route path="hubhus" element={<AdminHubhusPage />} />
        <Route path="survey" element={<AdminSurveyPage />} />
        <Route path="survey-log" element={<AdminSurveyLogPage />} />
        <Route path="survey-duplicates" element={<SurveyDuplicatesPage />} />
        <Route path="email" element={<AdminEmailTemplatePage />} />
        <Route path="tactics" element={<TacticsPage />} />
        <Route path="debug" element={<DebugPage />} />
        <Route path="categories" element={<ProductCategoriesPage />} />
        <Route path="knowledge" element={<ProductKnowledgeAdminPage />} />
        <Route path="experiment" element={<ExperimentDashboardPage />} />
        <Route path="email-flow" element={<EmailFlowPage />} />
        <Route path="satisfaction" element={<SatisfactionDashboard />} />
        <Route path="logs" element={<AdminLogsPage />} />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" />
        {/* PWA install prompt removed to prevent showing on customer pages */}
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
